import { FacetGroup, FacetExpansionState } from '../types';
import { HierarchicalFacetTree } from './HierarchicalFacetTree';
import { HierarchicalFacetNode } from '../types';
import { useState } from 'preact/hooks';
import {
  buildHierarchicalTree,
  convertLegacyFacetValues,
} from '../utils/facetTreeBuilder';

interface CategoricalFacetProps {
  facetGroup: FacetGroup;
  selectedFacets: { [key: string]: string[][] }; // Now supports hierarchical paths
  expandedFacets: FacetExpansionState;
  onFacetClick: (fieldName: string, facetPath: string[]) => void;
  onToggleExpansion: (fieldName: string, facetPath: string[]) => void;
}

function filterFacetNodes(
  nodes: HierarchicalFacetNode[],
  searchQuery: string
): HierarchicalFacetNode[] {
  if (!searchQuery.trim()) {
    return nodes;
  }

  const lowerQuery = searchQuery.toLowerCase();

  const filtered: HierarchicalFacetNode[] = [];

  for (const node of nodes) {
    const nodeMatches = node.displayValue.toLowerCase().includes(lowerQuery);
    const filteredChildren = node.children
      ? filterFacetNodes(node.children, searchQuery)
      : undefined;
    const hasMatchingDescendants =
      filteredChildren !== undefined && filteredChildren.length > 0;

    if (nodeMatches || hasMatchingDescendants) {
      filtered.push({
        ...node,
        children: filteredChildren,
        isExpanded: hasMatchingDescendants ? true : node.isExpanded,
      });
    }
  }

  return filtered;
}

export function CategoricalFacet({
  facetGroup,
  selectedFacets,
  expandedFacets,
  onFacetClick,
  onToggleExpansion,
}: CategoricalFacetProps) {
  const [searchQuery, setSearchQuery] = useState('');
  // Get selected and expanded paths for this facet
  const selectedPaths = selectedFacets[facetGroup.field_name] || [];
  const expandedPathsMap = expandedFacets[facetGroup.field_name] || {};
  const expandedPaths = Object.keys(expandedPathsMap)
    .filter((pathKey) => expandedPathsMap[pathKey])
    .map((pathKey) => pathKey.split('/'));

  // Handle potential backward compatibility: convert legacy tuple format to FacetValue
  const processedFacetType = { ...facetGroup.facet_type };
  if (processedFacetType.values) {
    // Check if we have the new FacetValue format or legacy tuple format
    const firstValue = processedFacetType.values[0];
    if (Array.isArray(firstValue)) {
      // Legacy tuple format [path, count] - convert to FacetValue
      processedFacetType.values = convertLegacyFacetValues(
        processedFacetType.values as unknown as [string, number][]
      );
    }
    // If it's already FacetValue objects, use them as-is
  }

  // Build hierarchical tree
  let treeNodes = buildHierarchicalTree(processedFacetType, {
    selectedPaths,
    expandedPaths,
  });

  // Apply search filter
  treeNodes = filterFacetNodes(treeNodes, searchQuery);

  const handleFacetClick = (path: string[]) => {
    onFacetClick(facetGroup.field_name, path);
  };

  const handleToggleExpansion = (path: string[]) => {
    onToggleExpansion(facetGroup.field_name, path);
  };

  const handleResetSearch = () => {
    setSearchQuery('');
  };

  return (
    <div key={facetGroup.field_name} className="facet-group">
      <div className="facet-group-header">
        <h3>{facetGroup.display_name}</h3>
        <div className="facet-search-wrapper">
          <input
            type="text"
            className="facet-search-input"
            placeholder="Cerca..."
            value={searchQuery}
            onInput={(e) =>
              setSearchQuery((e.target as HTMLInputElement).value)
            }
          />
          {searchQuery && (
            <button
              className="facet-search-reset"
              onClick={handleResetSearch}
              title="Cancella ricerca"
              aria-label="Cancella ricerca"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      <div className="facet-toggles">
        <HierarchicalFacetTree
          nodes={treeNodes}
          selectedPaths={selectedPaths}
          onFacetClick={handleFacetClick}
          onToggleExpansion={handleToggleExpansion}
        />
      </div>
    </div>
  );
}
